import type { Locale } from '@hub/contracts'
import { i18n } from '@/i18n'
import { intlLocale } from '@/i18n/dates'

/** BCP 47 tag for the active UI language (read at call time so switches apply). */
function tag(): string {
  return intlLocale(i18n.language as Locale)
}

/** Time-of-day in the active locale (day separators carry the date). */
export function formatTime(iso: string): string {
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return ''
  return date.toLocaleTimeString(tag(), { hour: '2-digit', minute: '2-digit' })
}

/** "Idag", "Igår" or a short date — the key used to group messages by day. */
export function formatDay(iso: string): string {
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return ''
  const today = new Date()
  const startOf = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime()
  const diffDays = Math.round((startOf(today) - startOf(date)) / 86_400_000)
  if (diffDays === 0) return i18n.t('common:time.today')
  if (diffDays === 1) return i18n.t('common:time.yesterday')
  return date.toLocaleDateString(tag(), {
    day: 'numeric',
    month: 'short',
    ...(date.getFullYear() !== today.getFullYear() ? { year: 'numeric' } : {}),
  })
}

/** "Sön, 30 aug" — weekday + date, shown next to the message actions. */
export function formatDayDate(iso: string): string {
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return ''
  const locale = tag()
  const weekday = date.toLocaleDateString(locale, { weekday: 'short' }).replace(/\.$/, '')
  const day = date
    .toLocaleDateString(locale, {
      day: 'numeric',
      month: 'short',
      ...(date.getFullYear() !== new Date().getFullYear() ? { year: 'numeric' } : {}),
    })
    .replace(/\.$/, '')
  return `${weekday.charAt(0).toUpperCase()}${weekday.slice(1)}, ${day}`
}

export function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}
